import {
  Clock20,
  EmailOutline20,
  Globe20,
  InstagramOutline20,
  Location20,
  Phone20,
  WhatsappOutline20
} from '@elfsight/icons';
import { clearUrl } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import type { ContactsItemProps } from './contacts-item';
import { ContactsItem } from './contacts-item';

const ellipsisOverflowStyles = {
  display: 'inline-block',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
};

export function ContactsStatusIndicator(props: ContactsItemProps) {
  const { t } = useLingui();

  return (
    <ContactsItem
      {...props}
      underlineAvailable={false}
      label={t`Hours`}
      icon={Clock20}
    />
  );
}

export function ContactsPhoneItem(props: ContactsItemProps) {
  const { t } = useLingui();

  return (
    <ContactsItem
      {...props}
      label={t`Phone`}
      value={props.value}
      icon={Phone20}
      href={`tel:${props.value}`}
    />
  );
}

export function ContactsEmailItem(props: ContactsItemProps) {
  const { t } = useLingui();

  return (
    <ContactsItem
      {...props}
      style={ellipsisOverflowStyles}
      label={t`Email`}
      value={props.value}
      icon={EmailOutline20}
      href={`mailto:${props.value}`}
    />
  );
}

export function ContactsWebsiteItem(props: ContactsItemProps) {
  const { t } = useLingui();

  return (
    <ContactsItem
      {...props}
      style={ellipsisOverflowStyles}
      label={t`Website`}
      value={clearUrl(String(props.value))}
      icon={Globe20}
      href={ensureHttps(String(props.value))}
      target="_blank"
    />
  );
}

export function ContactsAddressItem(props: ContactsItemProps) {
  const { t } = useLingui();

  return <ContactsItem {...props} label={t`Address`} icon={Location20} />;
}

export function ContactsWhatsAppItem(props: ContactsItemProps) {
  const { value } = props;

  if (!value || typeof value !== 'string') {
    return null;
  }

  const displayNumber = props.showLabel;
  const { link, phone } = getWhatsAppProps(value);

  return (
    <ContactsItem
      {...props}
      target="_top"
      label="WhatsApp"
      value={displayNumber ? phone : 'WhatsApp'}
      icon={WhatsappOutline20}
      href={`${link}`}
    />
  );
}

export function ContactsInstagramItem(props: ContactsItemProps) {
  const displayUsername = props.showLabel || props.showIcon;
  const username = getInstagramUsername(props.value.toString());

  return (
    <ContactsItem
      {...props}
      target="_top"
      label="Instagram"
      value={displayUsername ? `@${username}` : 'Instagram'}
      icon={InstagramOutline20}
      href={`https://www.instagram.com/${username}/`}
    />
  );
}

const getWhatsAppProps = (value: string) => {
  const linkRegExp = /(https?:\/\/)?([\w.-]+)\.([\w.]{2,6})([\w.-=?%+]*)\/?$/;
  const isLink = linkRegExp.test(value);
  const phone = value.replace(/[^0-9]/g, '');
  const displayedPhone = value.replace(/[^0-9+ ]/g, '');
  const link = isLink ? value : `https://wa.me/${phone}`;

  return { link, phone: displayedPhone };
};

const getInstagramUsername = (value: string) => {
  const linkRegExp =
    /^(?:https?:\/\/)?(?:www\.)?instagram.com\/([^/]+)\/?(\?[^$]+)?$/;
  const isLink = linkRegExp.test(value);

  return isLink ? value.match(linkRegExp)?.[1] : value.replace('@', '');
};

function ensureHttps(url: string) {
  const protocolPattern = /^(https?:\/\/)/i;
  if (!protocolPattern.test(url)) {
    url = 'https://' + url;
  }
  return url;
}
